import { useState, useEffect } from "react";
import { DragDropContext } from "@hello-pangea/dnd";
import { useDispatch, useSelector } from "react-redux";
import { setPuzzlePiece, setPlantelId, resetPlantelState } from "../../store/reducers/phraseBuilderPlantel/phraseBuilderPlantel.slice";

import PhraseSelectorsPlantel from "./PhraseSelectorsPlantel";
import PhraseListPlantel from "./PhraseListPlantel";
import PhrasePuzzlePlantel from "./PhrasePuzzlePlantel";
import SavedPhrasesPlantel from "./SavedPhrasesPlantel";
import Modal from "../UI/Modal";
import { Icon } from "@iconify/react/dist/iconify.js";

const PhraseBuilderLayoutPlantel = ({ plantelId }) => {
  const dispatch = useDispatch();
  const {
    puzzle,
    leftPhrases,
    rightPhrases,
    leftWord,
    rightWord,
    leftRandom,
    rightRandom,
  } = useSelector((s) => s.phraseBuilderPlantel);
  const [showErrorModal, setShowErrorModal] = useState(false);

  // Establecer el plantelId en el estado cuando el componente se monta
  useEffect(() => {
    if (plantelId) {
      dispatch(setPlantelId(plantelId));
    }
    
    // Cleanup: resetear el estado cuando se desmonta el componente
    return () => {
      dispatch(resetPlantelState());
    };
  }, [plantelId, dispatch]);

  const onDragEnd = ({ source, destination, draggableId }) => {
    if (!destination) return;

    const destSide = destination.droppableId;
    if (!["left", "right"].includes(destSide)) return;

    if (puzzle[destSide]) return;

    const item = JSON.parse(draggableId);

    // seguridad extra
    if (item.side !== destSide) return;

    // Validar que no sea la misma frase en ambos lados
    const oppositeSide = destSide === "left" ? "right" : "left";
    if (puzzle[oppositeSide] && puzzle[oppositeSide].id === item.id) {
      setShowErrorModal(true);
      return;
    }

    dispatch(setPuzzlePiece({ side: destSide, item }));
  };

  return (
    <div className="container-fluid py-4 py-md-5">
      <div className="px-3" style={{ maxWidth: "100%" }}>
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body py-4">
            <div className="text-center mb-2">
              <h2 className="mb-1 fw-bold">Combinaciones de frases del plantel</h2>
              <p className="mb-2 text-muted small">
                Crea combinaciones personalizadas de talentos para este plantel
              </p>

              <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap mt-3">
                {leftWord || leftRandom ? (
                  <span
                    className={`badge ${leftRandom ? "bg-info" : "bg-primary"} d-flex align-items-center gap-1`}
                  >
                    <Icon icon="mdi:chevron-left" width={16} />
                    {leftRandom ? "Random" : leftWord.label}
                  </span>
                ) : (
                  <span className="badge bg-secondary d-flex align-items-center gap-1">
                    <Icon icon="mdi:chevron-left" width={16} />
                    Sin seleccionar
                  </span>
                )}

                <span className="text-muted fw-bold">VS</span>

                {rightWord || rightRandom ? (
                  <span
                    className={`badge ${rightRandom ? "bg-info" : "bg-success"} d-flex align-items-center gap-1`}
                  >
                    {rightRandom ? "Random" : rightWord.label}
                    <Icon icon="mdi:chevron-right" width={16} />
                  </span>
                ) : (
                  <span className="badge bg-secondary d-flex align-items-center gap-1">
                    Sin seleccionar
                    <Icon icon="mdi:chevron-right" width={16} />
                  </span>
                )}
              </div>
            </div>

            <div className="row g-3 mt-2">
              <div className="col-md-4">
                <div className="d-flex align-items-start gap-2">
                  <div className="bg-primary bg-opacity-10 rounded-circle p-2 flex-shrink-0">
                    <Icon
                      icon="mdi:numeric-1-circle"
                      width={20}
                      className="text-primary"
                    />
                  </div>
                  <div>
                    <p className="mb-0 fw-semibold small">
                      Selecciona talentos
                    </p>
                    <p
                      className="mb-0 text-muted"
                      style={{ fontSize: "0.75rem" }}
                    >
                      Elige palabras de izquierda y derecha
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="d-flex align-items-start gap-2">
                  <div className="bg-primary bg-opacity-10 rounded-circle p-2 flex-shrink-0">
                    <Icon
                      icon="mdi:numeric-2-circle"
                      width={20}
                      className="text-primary"
                    />
                  </div>
                  <div>
                    <p className="mb-0 fw-semibold small">Arrastra frases</p>
                    <p
                      className="mb-0 text-muted"
                      style={{ fontSize: "0.75rem" }}
                    >
                      Coloca las frases en las zonas correspondientes
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="d-flex align-items-start gap-2">
                  <div className="bg-primary bg-opacity-10 rounded-circle p-2 flex-shrink-0">
                    <Icon
                      icon="mdi:numeric-3-circle"
                      width={20}
                      className="text-primary"
                    />
                  </div>
                  <div>
                    <p className="mb-0 fw-semibold small">
                      Guarda configuración
                    </p>
                    <p
                      className="mb-0 text-muted"
                      style={{ fontSize: "0.75rem" }}
                    >
                      Crea tu combinación personalizada
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4 align-items-start">
          {/* COLUMNA IZQUIERDA COMPLETA */}
          <div className="col-12 col-lg-8">
            <PhraseSelectorsPlantel plantelId={plantelId} />

            <DragDropContext onDragEnd={onDragEnd}>
              <PhrasePuzzlePlantel />
              <PhraseListPlantel />
            </DragDropContext>
          </div>

          {/* COLUMNA DERECHA */}
          <div className="col-12 col-lg-4">
            <div style={{ position: "sticky", top: 20 }}>
              <SavedPhrasesPlantel plantelId={plantelId} />
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Frase duplicada"
        size="sm"
      >
        <div className="text-center py-3">
          <Icon
            icon="mdi:alert-circle-outline"
            className="text-warning"
            width={64}
          />
          <p className="mb-4 mt-3">
            No puedes seleccionar la <strong>misma frase</strong> en ambas
            zonas. Por favor, elige frases diferentes para cada lado.
          </p>
          <div className="d-flex justify-content-center">
            <button
              type="button"
              className="btn btn-primary px-4 py-2"
              onClick={() => setShowErrorModal(false)}
            >
              Entendido
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PhraseBuilderLayoutPlantel;
